import { Button } from '@/components/ui/button';
import posthog from 'posthog-js';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ModeToggle } from '../shared/mode-toggle';
import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';
import { LogOut, User as UserIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface NavbarProps {
  user?: User | null;
  minimal?: boolean;
}

export const Navbar = ({ user, minimal }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthPage = location.pathname === '/auth';
  const mode = new URLSearchParams(location.search).get('mode') || 'login';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <>
      {/* Skip link for screen readers */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-primary-foreground px-4 py-2 rounded z-50"
      >
        Skip to main content
      </a>

      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          scrolled
            ? "bg-background/80 backdrop-blur-md border-b border-border"
            : "bg-transparent border-b border-transparent"
        )}
      >
        <div className="container flex h-16 items-center justify-between">
          {/* Left side - Enhanced Logo */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group min-w-0 shrink-0">
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:rotate-6">
                <img src="/assets/chytbot-mark-white-128.png" alt="" aria-hidden="true" className="w-5 h-5" />
              </div>
              <span className="font-display font-bold text-lg sm:text-xl tracking-tight truncate">ChytBot</span>
            </Link>

            {!minimal && !user && (
              <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
                <Link to="/pricing" className="text-muted-foreground hover:text-primary focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded-sm px-1 -mx-1 transition-colors">
                  Pricing
                </Link>
                <Link to="/integrations" className="text-muted-foreground hover:text-primary focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded-sm px-1 -mx-1 transition-colors">
                  Integrations
                </Link>
                <Link to="/contact" className="text-muted-foreground hover:text-primary focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded-sm px-1 -mx-1 transition-colors">
                  Contact
                </Link>
              </nav>
            )}
          </div>

          {/* Right side - Navigation and Profile */}
          <div className="flex items-center gap-3">
            <ModeToggle />

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full bg-muted border p-0 overflow-hidden">
                    <span className="text-xs font-bold">{user.email?.charAt(0).toUpperCase()}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.user_metadata.full_name || 'User'}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <UserIcon className="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:bg-destructive focus:text-destructive-foreground" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : isAuthPage ? (
              <Button
                onClick={() => navigate(`/auth?mode=${mode === 'login' ? 'signup' : 'login'}`)}
                variant="cta"
                size="lg"
                className="shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-all"
              >
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/auth?mode=login')}
                  variant="ghost"
                  size="lg"
                  className="hidden sm:inline-flex text-muted-foreground hover:text-foreground"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => {
                    posthog.capture('cta_clicked', { location: 'navbar', target: 'signup' });
                    navigate('/auth?mode=signup');
                  }}
                  variant="cta"
                  size="lg"
                  className="shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-all"
                >
                  Get Started Free
                </Button>
              </>
            )}

            {/* Mobile Menu - Sheet drawer */}
            {!minimal && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    aria-label="Open navigation menu"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right">
                  <SheetHeader>
                    <SheetTitle asChild>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center shrink-0">
                          <img src="/assets/chytbot-mark-white-128.png" alt="" aria-hidden="true" className="w-5 h-5" />
                        </div>
                        <span className="font-display font-bold text-xl tracking-tight">ChytBot</span>
                      </div>
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="mt-6 flex flex-col gap-1">
                    <Link
                      to="/pricing"
                      className="text-muted-foreground hover:text-foreground text-base py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-colors"
                    >
                      Pricing
                    </Link>
                    <Link
                      to="/integrations"
                      className="text-muted-foreground hover:text-foreground text-base py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-colors"
                    >
                      Integrations
                    </Link>
                    <Link
                      to="/contact"
                      className="text-muted-foreground hover:text-foreground text-base py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-colors"
                    >
                      Contact
                    </Link>
                    <div className="pt-4 mt-2 border-t border-border space-y-2">
                      <Button
                        variant="cta"
                        className="w-full min-h-[44px]"
                        onClick={() => {
                          posthog.capture('cta_clicked', { location: 'navbar_mobile', target: 'signup' });
                          navigate('/auth?mode=signup');
                        }}
                      >
                        Get Started Free
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full min-h-[44px] text-muted-foreground"
                        onClick={() => navigate('/auth?mode=login')}
                      >
                        Log in
                      </Button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
